import './style.modules.scss'

import { BrandIcon } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import React, { useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { CommitmentsProps } from './Commitments.type'
import type { CommitmentsBackgroundColor } from './CommitmentsColor.type'

export const Commitments: React.FC<CommitmentsProps> = (
  props: CommitmentsProps
) => {
  const { tab, headline, testId } = props
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const buttonRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const focusTargetRef = useRef<'tab' | 'button' | null>(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const tabCount = tab.length
  const hasMounted = useRef(false)

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }

    tabRefs.current[activeTabIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
    if (focusTargetRef.current === 'tab') {
      tabRefs.current[activeTabIndex]?.focus()
    } else if (focusTargetRef.current === 'button') {
      buttonRefs.current[activeTabIndex]?.focus()
    }
    focusTargetRef.current = null
  }, [activeTabIndex])

  const handleTabClick = (index: number) => {
    tabRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
    setActiveTabIndex(index)
  }

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>,
    origin: 'button' | 'tabpanel'
  ) => {
    switch (event.key) {
      case 'ArrowLeft':
        setActiveTabIndex(prev => (prev === 0 ? tabCount - 1 : prev - 1))
        break
      case 'ArrowRight':
        setActiveTabIndex(prev => (prev === tabCount - 1 ? 0 : prev + 1))
        break
      case 'Home':
        setActiveTabIndex(0)
        break
      case 'End':
        setActiveTabIndex(tabCount - 1)
        break
      case 'Tab':
        if (event.shiftKey) {
          if (origin === 'tabpanel') {
            if (activeTabIndex === 0) {
              return
            }
            focusTargetRef.current = 'button'
            setActiveTabIndex(prev => (prev === 0 ? 0 : prev - 1))
            event.preventDefault()
            return
          }
          if (origin === 'button') {
            const ref = tabRefs.current[activeTabIndex]
            if (ref) {
              ref.focus()
              event.preventDefault()
            }
          }
        } else {
          if (origin === 'tabpanel') {
            const ref = buttonRefs.current[activeTabIndex]
            if (ref) {
              ref.focus()
              event.preventDefault()
            } else if (activeTabIndex < tabCount - 1) {
              focusTargetRef.current = 'tab'
              setActiveTabIndex(prev => prev + 1)
              event.preventDefault()
            }
            return
          }

          if (origin === 'button') {
            if (activeTabIndex < tabCount - 1) {
              focusTargetRef.current = 'tab'
              setActiveTabIndex(prev => prev + 1)
              event.preventDefault()
            }
          }
        }
        break
      default:
        break
    }
  }

  const getTabColor = (bgColor?: CommitmentsBackgroundColor) => {
    switch (bgColor) {
      case BackgroundColor.MINT_TINT:
        return 'u-bgColorMintLight10'
      case BackgroundColor.PURPLE_TINT:
        return 'u-bgColorPurpleLight10'
      case BackgroundColor.LIME_TINT:
        return 'u-bgColorLimeLight20'
      case BackgroundColor.STONE:
        return 'u-bgColorStone'
      case BackgroundColor.BERRY_TINT:
        return 'u-bgColorBerryLight10'
      case BackgroundColor.ORANGE_TINT:
        return 'u-bgColorOrangeLight20'
      default:
        return 'u-bgColorWhite'
    }
  }

  if (!tab || tabCount < 2 || tabCount > 4) {
    console.error('Invalid commitments tab length')
    return null
  }

  return (
    <div className="Commitments Commitments--new u-posRelative">
      <h2 className="Type-2 u-sizeConstrained u-flex u-flexJustifyCenter u-textCenter u-paddingBottom48 u-textPrimary u-textColorNeutralGrey">
        {headline}
      </h2>
      <div className="Tabs-container Tab-items-height u-sizeConstrained Grid u-marginCenter">
        <div
          className="Tabs Content u-flex u-flexRow u-flexJustifyCenter"
          role="tablist"
        >
          {tab.map((tab, index) => (
            <div className="Tab-item u-paddingHoriz8" key={'tab' + index}>
              <button
                role="tab"
                aria-controls={`tabpanel-${tab.identifier}`}
                aria-selected={activeTabIndex === index}
                tabIndex={activeTabIndex === index ? 0 : -1}
                className={`Commitments-tab u-sm-marginTop4${
                  activeTabIndex === index ? ' Item-active' : ''
                }`}
                data-analytics={tab.analytics}
                ref={el => (tabRefs.current[index] = el)}
                onClick={() => handleTabClick(index)}
                onKeyDown={e => handleTabKeyDown(e, 'tabpanel')}
                data-testid={testId ? `${testId}-tab-${index}` : undefined}
              >
                <BrandIcon
                  icon={tab.icon}
                  color={tab.iconColor}
                  fill={tab.fill ? true : false}
                  stroke={tab.stroke ? true : false}
                  className="Button-icon u-paddingBottom8 u-block u-marginCenter"
                  data-testid={
                    testId ? `${testId}-${index}-tab-icon` : undefined
                  }
                />
                {tab.title}
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="Tab-panels u-marginBottom32">
        {tab.map((tab, index) => (
          <div
            role="tabpanel"
            className={`Tab-panel${
              activeTabIndex === index
                ? ''
                : ' u-md-hidden u-lg-hidden u-sm-hidden'
            }`}
            key={'tabpanel-' + index}
            aria-labelledby={tab.buttonId}
            hidden={activeTabIndex !== index}
          >
            <div className="Commitments-container u-sizeConstrained">
              <div className="Tab-content Content Grid u-flexJustifyCenter">
                <div
                  className={
                    'Image-box u-sm-hidden u-md-size1of2 u-lg-size1of2' +
                    (tab.location === 'right' ? ' u-flexOrderLast' : '')
                  }
                >
                  <img
                    className="Commitments-image"
                    src={tab.content.imageSrc}
                    alt={tab.content.imageAlt}
                    data-testid={
                      testId ? `${testId}-${index}-image` : undefined
                    }
                  />
                </div>
                <div
                  className={`Commitments-body Content u-paddingVert48 u-paddingHoriz40 u-md-size1of2 u-lg-size1of2 ${getTabColor(
                    tab.bgcolor
                  )}`}
                  data-testid={
                    testId ? `${testId}-${index}-content` : undefined
                  }
                >
                  <h3
                    className="u-textColorNeutralGrey u-textBold"
                    data-testid={
                      testId ? `${testId}-${index}-heading` : undefined
                    }
                  >
                    {tab.content.heading}
                  </h3>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: xss(tab.content.body)
                    }}
                    data-testid={testId ? `${testId}-${index}-body` : undefined}
                  ></p>
                  <div>
                    {tab.content.link &&
                      tab.content.linkText &&
                      tab.content.linkText.length > 0 && (
                        <a
                          className={`Commitments-button-${index} Button Button--primary`}
                          href={tab.content.link}
                          data-analytics={tab.analytics}
                          data-testid={
                            testId ? `${testId}-${index}-link` : undefined
                          }
                          onKeyDown={e => handleTabKeyDown(e, 'button')}
                          ref={el => (buttonRefs.current[index] = el)}
                        >
                          {tab.content.linkText}
                        </a>
                      )}
                  </div>
                </div>
              </div>
            </div>
            <div className="Carousel-toggle u-sizeConstrained u-marginLeftM8 u-flex u-flexAlignItemsEnd u-Mobile u-lg-hidden u-md-hidden u-paddingVert8">
              <button
                id="carousel-previous"
                aria-hidden="false"
                className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-marginRight16 u-flex u-padding0"
                onClick={() =>
                  setActiveTabIndex(index === 0 ? tabCount - 1 : index - 1)
                }
                disabled={index === 0}
              >
                <span
                  className="adel-material-symbols-24 u-textColorActionBlue"
                  title="Slide Left"
                  aria-label="Slide Left"
                >
                  chevron_left
                </span>
              </button>
              <button
                id="carousel-next"
                aria-hidden="false"
                className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-marginRight16 u-flex u-padding0"
                onClick={() =>
                  setActiveTabIndex(index === tabCount - 1 ? 0 : index + 1)
                }
                disabled={index === tabCount - 1}
              >
                <span
                  className="adel-material-symbols-24 u-textColorActionBlue"
                  title="Slide Right"
                  aria-label="Slide Right"
                >
                  chevron_right
                </span>
              </button>
              <p className="u-textColorActionBlue u-paddingLeft0">
                {index + 1} / {tabCount}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
